import { useEffect, useState } from "react";

import { listBranches } from "./branches";

/**
 * Whether this clinic has any location at all.
 *
 * ── The gap this closes ─────────────────────────────────────────────────────
 *
 * `useActiveBranch` returns `null` for two completely different situations —
 * *the switcher has not resolved one yet* and *there are none to resolve* —
 * and its own comment tells callers to treat null as "not yet". That is right
 * for a clinic that has locations, and it was the only case that existed while
 * a clinic could be created only by the seed or an operator, both of which make
 * one immediately.
 *
 * **Self-serve sign-up made the other case ordinary**, and the calendar showed
 * what that costs: a brand-new practice opening it got *"Choosing a branch…"*
 * forever. A loading message that never finishes is worse than an error — it
 * says the product is working on something, so the reader waits instead of
 * acting, and the one thing they needed to be told is that they have not opened
 * a location yet.
 *
 * ── Why a second call is acceptable ─────────────────────────────────────────
 *
 * `GET /branches` is clinic-scoped, tiny, and already fetched by the branch
 * switcher on every page — this shares the browser's cache with it rather than
 * adding a round trip in practice. **Threading it through a context was
 * rejected for now**: it would mean the shell owning state two screens read,
 * and the switcher's own load is deliberately independent so a failure there
 * cannot blank the page it frames.
 */
export type BranchAvailability = "loading" | "none" | "some" | "unknown";

export function useHasBranches(): BranchAvailability {
  const [state, setState] = useState<BranchAvailability>("loading");

  useEffect(() => {
    const controller = new AbortController();

    listBranches(controller.signal)
      .then((branches) => {
        if (!controller.signal.aborted) setState(branches.length === 0 ? "none" : "some");
      })
      .catch(() => {
        // **`unknown`, not `none`.** An unreachable server must never make a
        // clinic that has locations be told it has none — the wrong empty
        // state is a support call, and the caller can fall back to whatever it
        // showed before this hook existed.
        if (!controller.signal.aborted) setState("unknown");
      });

    return () => controller.abort();
  }, []);

  return state;
}
